import { useState, useCallback, useEffect } from 'react'

declare global {
  interface Window {
    esales?: {
      window?: {
        setFullScreen: (full: boolean) => Promise<void>
        isFullScreen: () => Promise<boolean>
      }
    }
  }
}

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const isElectron = typeof window !== 'undefined' && !!window.esales?.window

  const refreshFullscreenState = useCallback(async () => {
    if (isElectron && window.esales?.window) {
      try {
        const full = await window.esales.window.isFullScreen()
        setIsFullscreen(!!full)
      } catch {
        setIsFullscreen(false)
      }
    } else {
      setIsFullscreen(!!document.fullscreenElement)
    }
  }, [isElectron])

  const setFullscreen = useCallback(
    async (full: boolean) => {
      if (isElectron && window.esales?.window) {
        await window.esales.window.setFullScreen(full)
        await refreshFullscreenState()
        return
      }
      if (full) {
        await document.documentElement.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
      await refreshFullscreenState()
    },
    [isElectron, refreshFullscreenState]
  )

  const toggle = useCallback(async () => {
    if (isElectron && window.esales?.window) {
      const full = await window.esales.window.isFullScreen()
      await setFullscreen(!full)
      return
    }
    await setFullscreen(!document.fullscreenElement)
  }, [setFullscreen, isElectron])

  useEffect(() => {
    refreshFullscreenState()
    if (!isElectron) {
      const handler = () => setIsFullscreen(!!document.fullscreenElement)
      document.addEventListener('fullscreenchange', handler)
      return () => document.removeEventListener('fullscreenchange', handler)
    }
    const interval = setInterval(refreshFullscreenState, 500)
    return () => clearInterval(interval)
  }, [isElectron, refreshFullscreenState])

  return { isFullscreen, setFullscreen, toggle, refreshFullscreenState }
}
